import { Injectable, computed, signal } from '@angular/core';
import { LoadingState } from './loading.model';

/**
 * Global loading state management service
 * @description Manages the application-wide page loader state using Angular signals.
 * Used to show/hide the full-page loading overlay during async operations.
 *
 * @example
 * ```typescript
 * // Show loading overlay
 * loadingService.show('LOADING');
 *
 * // Hide loading overlay
 * loadingService.hide();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  /** Internal signal managing the loading state */
  private readonly loadingState = signal<LoadingState>({
    isLoading: false,
    message: 'COMMON.LOADING',
  });

  /** Reference counter tracking concurrent show() calls */
  private readonly loadingCount = signal(0);

  /** Computed signal exposing whether loading is active */
  readonly isLoading = computed(() => this.loadingState().isLoading);

  /** Computed signal exposing the current loading message translation key */
  readonly message = computed(() => this.loadingState().message);

  /**
   * Shows the global page loader
   * @param message - i18n translation key for the loading message (default: 'COMMON.LOADING')
   */
  show(message = 'COMMON.LOADING'): void {
    this.loadingCount.update((count) => count + 1);
    this.loadingState.set({ isLoading: true, message });
  }

  /**
   * Hides the global page loader
   */
  hide(): void {
    this.loadingCount.update((count) => Math.max(0, count - 1));
    if (this.loadingCount() === 0) {
      this.loadingState.set({ isLoading: false, message: '' });
    }
  }

  /**
   * Toggles the loading state
   * @param message - Optional i18n translation key for the loading message
   */
  toggle(message = 'COMMON.LOADING'): void {
    if (this.loadingState().isLoading) {
      this.hide();
    } else {
      this.show(message);
    }
  }
}
